import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { formatTime12h } from "@/lib/time";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Loader2,
  LogOut,
  Scissors,
  MoreVertical,
  RefreshCw,
  ArrowLeft,
  MessageSquare,
  Send,
  CheckCircle2,
  AlertCircle,
  Info,
} from "lucide-react";

function LoginForm() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) setErr(res.error);
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center px-6 bg-stone-950"
    >
      <div className="w-full max-w-md border border-stone-800 bg-stone-900/60 p-8 md:p-10">
        <div className="flex items-center gap-2 mb-8">
          <Scissors size={16} className="text-amber-500" />
          <span className="font-display tracking-tight text-stone-100">
            Andooozy<span className="text-amber-500">Cutzz</span>
          </span>
          <span className="ml-2 text-[10px] tracking-[0.3em] uppercase text-stone-500">
            Admin
          </span>
        </div>
        <h1 className="font-display text-3xl text-stone-50 mb-2">
          Andaz, welcome back.
        </h1>
        <p className="text-sm text-stone-400 mb-8">
          Sign in to view your upcoming bookings.
        </p>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="text-[10px] tracking-[0.3em] uppercase text-stone-400">
              Email
            </Label>
            <Input
              data-testid="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-2 bg-stone-900 border-stone-800 rounded-none focus-visible:ring-1 focus-visible:ring-amber-500 focus-visible:border-amber-500 text-stone-50"
            />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.3em] uppercase text-stone-400">
              Password
            </Label>
            <Input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-2 bg-stone-900 border-stone-800 rounded-none focus-visible:ring-1 focus-visible:ring-amber-500 focus-visible:border-amber-500 text-stone-50"
            />
          </div>
          {err && (
            <div
              data-testid="admin-login-error"
              className="text-sm text-red-400 border border-red-900/40 bg-red-900/10 px-3 py-2"
            >
              {err}
            </div>
          )}
          <Button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-amber-500 text-stone-950 hover:bg-amber-400 rounded-none py-3 text-xs tracking-[0.2em] uppercase font-medium"
          >
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Signing in…
              </>
            ) : (
              "Sign in"
            )}
          </Button>
        </form>
        <Link
          to="/"
          data-testid="admin-back-home"
          className="mt-8 flex items-center gap-2 text-xs text-stone-500 hover:text-amber-500 transition-colors duration-300"
        >
          <ArrowLeft size={12} /> Back to site
        </Link>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upcomingOnly, setUpcomingOnly] = useState(true);
  const [smsLogs, setSmsLogs] = useState([]);
  const [smsProviderActive, setSmsProviderActive] = useState(false);
  const [runningReminders, setRunningReminders] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [bkRes, smsRes] = await Promise.all([
        api.get("/admin/bookings", { params: { upcoming: upcomingOnly } }),
        api.get("/admin/sms-logs", { params: { limit: 15 } }),
      ]);
      setBookings(bkRes.data);
      setSmsLogs(smsRes.data.logs || []);
      setSmsProviderActive(!!smsRes.data.provider_active);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load bookings"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcomingOnly]);

  const runReminders = async () => {
    setRunningReminders(true);
    try {
      await api.post("/admin/cron/run-reminders");
      toast.success("Reminder job queued");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to queue reminders"));
    } finally {
      setRunningReminders(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      toast.success(`Booking ${status}`);
    } catch (e) {
      toast.error(formatApiError(e, "Update failed"));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Booking deleted");
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  };

  const totalRevenue = bookings
    .filter((b) => b.status !== "cancelled")
    .reduce((sum, b) => sum + b.total_price, 0);

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-stone-950 text-stone-50"
    >
      <header className="border-b border-stone-800 bg-stone-950/70 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Scissors size={16} className="text-amber-500" />
            <span className="font-display tracking-tight">
              Andooozy<span className="text-amber-500">Cutzz</span>
            </span>
            <span className="ml-2 text-[10px] tracking-[0.3em] uppercase text-stone-500">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-stone-400 hidden md:inline">
              {user?.email}
            </span>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="flex items-center gap-2 border border-stone-800 hover:border-amber-500/50 hover:text-amber-500 text-stone-300 px-4 py-2 text-xs tracking-[0.2em] uppercase transition-colors duration-300"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 md:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-amber-500/80 mb-3">
              Dashboard
            </div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight text-stone-50 leading-tight">
              Your <span className="italic text-amber-500">chair</span>, at a
              glance.
            </h1>
          </div>
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="flex items-center gap-2 border border-stone-800 hover:border-amber-500/50 hover:text-amber-500 text-stone-300 px-4 py-2 text-xs tracking-[0.2em] uppercase transition-colors duration-300 self-start"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 border border-stone-800 mb-10">
          <div className="p-6 border-b md:border-b-0 md:border-r border-stone-800">
            <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">
              Bookings shown
            </div>
            <div className="font-display text-3xl mt-2 text-stone-50">
              {bookings.length}
            </div>
          </div>
          <div className="p-6 border-b md:border-b-0 md:border-r border-stone-800">
            <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">
              Expected revenue
            </div>
            <div className="font-display text-3xl mt-2 text-amber-500">
              ${totalRevenue}
            </div>
          </div>
          <div className="p-6 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">
                View
              </div>
              <div className="font-display text-xl mt-2 text-stone-50">
                {upcomingOnly ? "Upcoming only" : "All bookings"}
              </div>
            </div>
            <button
              data-testid="toggle-upcoming"
              onClick={() => setUpcomingOnly((v) => !v)}
              className="text-xs tracking-[0.2em] uppercase text-amber-500 hover:text-amber-400 transition-colors duration-300"
            >
              {upcomingOnly ? "Show all" : "Upcoming"}
            </button>
          </div>
        </div>

        {/* Bookings */}
        <div className="border border-stone-800 bg-stone-900/40">
          {loading ? (
            <div className="p-10 text-stone-400 flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          ) : bookings.length === 0 ? (
            <div
              data-testid="admin-empty"
              className="p-10 text-stone-500 text-sm"
            >
              No bookings to show yet. Once clients book, they will appear
              here.
            </div>
          ) : (
            <Table data-testid="bookings-table">
              <TableHeader>
                <TableRow className="border-stone-800 hover:bg-transparent">
                  <TableHead className="text-[10px] tracking-[0.25em] uppercase text-stone-500">
                    When
                  </TableHead>
                  <TableHead className="text-[10px] tracking-[0.25em] uppercase text-stone-500">
                    Client
                  </TableHead>
                  <TableHead className="text-[10px] tracking-[0.25em] uppercase text-stone-500">
                    Service
                  </TableHead>
                  <TableHead className="text-[10px] tracking-[0.25em] uppercase text-stone-500">
                    Contact
                  </TableHead>
                  <TableHead className="text-[10px] tracking-[0.25em] uppercase text-stone-500 text-right">
                    Price
                  </TableHead>
                  <TableHead className="text-[10px] tracking-[0.25em] uppercase text-stone-500">
                    Status
                  </TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.map((b) => (
                  <TableRow
                    key={b.id}
                    data-testid={`booking-row-${b.id}`}
                    className="border-stone-800 hover:bg-stone-900/60"
                  >
                    <TableCell>
                      <div className="font-display text-stone-50">
                        {b.date}
                      </div>
                      <div className="text-xs text-amber-500">{formatTime12h(b.time)}</div>
                    </TableCell>
                    <TableCell className="text-stone-50">{b.name}</TableCell>
                    <TableCell className="text-stone-300">
                      {b.service_name}
                      <div className="text-xs text-stone-500">
                        {b.duration_min} min
                      </div>
                    </TableCell>
                    <TableCell className="text-stone-300">
                      <div>{b.phone}</div>
                      <div className="text-xs text-stone-500">{b.email}</div>
                    </TableCell>
                    <TableCell className="text-right font-display text-amber-500">
                      ${b.total_price}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${
                          b.status === "cancelled"
                            ? "border-red-900 text-red-400"
                            : b.status === "pending"
                              ? "border-stone-700 text-stone-400"
                              : "border-amber-500/60 text-amber-500"
                        }`}
                      >
                        {b.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`row-menu-${b.id}`}
                            className="text-stone-400 hover:text-amber-500 p-2 transition-colors duration-300"
                          >
                            <MoreVertical size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-stone-900 border-stone-800 rounded-none text-stone-200"
                        >
                          <DropdownMenuItem
                            data-testid={`mark-confirmed-${b.id}`}
                            onClick={() => updateStatus(b.id, "confirmed")}
                            className="rounded-none focus:bg-amber-500/10 focus:text-amber-500"
                          >
                            Mark confirmed
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            data-testid={`mark-pending-${b.id}`}
                            onClick={() => updateStatus(b.id, "pending")}
                            className="rounded-none focus:bg-amber-500/10 focus:text-amber-500"
                          >
                            Mark pending
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            data-testid={`mark-cancelled-${b.id}`}
                            onClick={() => updateStatus(b.id, "cancelled")}
                            className="rounded-none focus:bg-amber-500/10 focus:text-amber-500"
                          >
                            Cancel booking
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-stone-800" />
                          <DropdownMenuItem
                            data-testid={`delete-${b.id}`}
                            onClick={() => remove(b.id)}
                            className="rounded-none text-red-400 focus:bg-red-900/20 focus:text-red-400"
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* SMS activity */}
        <section
          data-testid="sms-activity"
          className="mt-14"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-amber-500/80 mb-2">
                SMS Activity
              </div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight text-stone-50 leading-tight">
                Twilio <span className="italic text-amber-500">reminders</span>
              </h2>
              <div
                data-testid="sms-provider-status"
                className="mt-3 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase px-3 py-1 border"
                style={{
                  borderColor: smsProviderActive ? "#f59e0b99" : "#57534e",
                  color: smsProviderActive ? "#f59e0b" : "#a8a29e",
                }}
              >
                {smsProviderActive ? (
                  <>
                    <CheckCircle2 size={12} /> Live · Twilio configured
                  </>
                ) : (
                  <>
                    <Info size={12} /> Demo mode · Add TWILIO_* env vars to go live
                  </>
                )}
              </div>
            </div>
            <button
              data-testid="run-reminders-now"
              disabled={runningReminders}
              onClick={runReminders}
              className="flex items-center gap-2 border border-amber-500/60 text-amber-500 hover:bg-amber-500/10 px-4 py-2 text-xs tracking-[0.2em] uppercase transition-colors duration-300 disabled:opacity-50 self-start"
            >
              {runningReminders ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Send size={12} />
              )}
              Run reminders now
            </button>
          </div>

          <div className="border border-stone-800 bg-stone-900/40">
            {smsLogs.length === 0 ? (
              <div className="p-8 text-stone-500 text-sm flex items-center gap-2">
                <MessageSquare size={14} /> No SMS activity yet. New bookings
                and nightly reminders will appear here.
              </div>
            ) : (
              <ul className="divide-y divide-stone-800">
                {smsLogs.map((s, i) => {
                  const statusColor =
                    s.status === "sent"
                      ? "text-amber-500 border-amber-500/60"
                      : s.status === "failed"
                        ? "text-red-400 border-red-900"
                        : "text-stone-400 border-stone-700";
                  const icon =
                    s.status === "failed" ? (
                      <AlertCircle size={14} className="text-red-400" />
                    ) : (
                      <MessageSquare size={14} className="text-amber-500" />
                    );
                  return (
                    <li
                      key={i}
                      data-testid={`sms-log-${i}`}
                      className="p-4 md:p-5 flex flex-col md:flex-row md:items-start gap-3"
                    >
                      <div className="flex items-center gap-3 md:w-56 shrink-0">
                        {icon}
                        <div>
                          <div className="text-xs text-stone-500">
                            {s.purpose === "owner_new_booking"
                              ? "New booking → Andaz"
                              : s.purpose === "client_reminder"
                                ? "Reminder → Client"
                                : s.purpose}
                          </div>
                          <div className="text-stone-300 text-sm font-display">
                            {s.to}
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 text-sm text-stone-400 leading-relaxed">
                        {s.body}
                        {s.error && (
                          <div className="mt-1 text-xs text-red-400">
                            {s.error}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-start md:items-end gap-1 md:w-40 shrink-0">
                        <span
                          className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${statusColor}`}
                        >
                          {s.status}
                        </span>
                        <span className="text-[10px] text-stone-600">
                          {new Date(s.created_at).toLocaleString()}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center text-stone-400">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }
  if (!user) return <LoginForm />;
  return <AdminDashboard />;
}
